import { useCallback, useEffect, useState } from "react";
import { CalendarDays, Plus, Loader2, LogIn, LogOut, XCircle, Clock, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { VisitDetailDialog } from "../components/VisitDetailDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_BADGE = {
  scheduled: "bg-[#D6E0D9] text-[#2E5C50]",
  in_progress: "bg-[#FDF3E1] text-[#C48231]",
  completed: "bg-[#E6F0EB] text-[#34745C]",
  missed: "bg-[#FCEAE8] text-[#B54D40]",
  cancelled: "bg-[#F9F7F4] border border-[#E8E6E1] text-[#57605A]",
};

const TABS = [
  { key: "today", label: "Today" },
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
  { key: "all", label: "All" },
];

const EMPTY = { client_id: "", caregiver_id: "", date: "", start_time: "09:00", end_time: "11:00", visit_type: "Personal Care", notes: "", required_tasks: "" };

const getGPS = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { timeout: 4000 }
    );
  });

const inputCls =
  "w-full bg-white border border-[#E8E6E1] rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]";

const localToday = () => new Date().toLocaleDateString("en-CA");

export default function SchedulePage() {
  const { user } = useAuth();
  const canManage = user.role !== "family";
  const [schedules, setSchedules] = useState(null);
  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [visitTypes, setVisitTypes] = useState(["Personal Care"]);
  const [tab, setTab] = useState("today");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [checkoutFor, setCheckoutFor] = useState(null);
  const [checkoutNotes, setCheckoutNotes] = useState("");
  const [checkoutTasks, setCheckoutTasks] = useState([]);
  const [detailFor, setDetailFor] = useState(null);

  const load = useCallback(() => api.get("/schedules").then((r) => setSchedules(r.data)), []);

  useEffect(() => {
    load();
    api.get("/visit-types").then((r) => setVisitTypes(r.data));
    if (canManage) {
      api.get("/clients").then((r) => setClients(r.data));
      api.get("/caregivers").then((r) => setCaregivers(r.data.filter((c) => c.status === "active")));
    }
  }, [load, canManage]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const today = localToday();
  const filtered = (schedules || []).filter((s) => {
    if (tab === "today") return s.date === today;
    if (tab === "upcoming") return s.date >= today && ["scheduled", "in_progress"].includes(s.status);
    if (tab === "past") return s.date < today;
    return true;
  });
  const sorted = [...filtered].sort((a, b) =>
    tab === "past" ? b.date.localeCompare(a.date) || a.start_time.localeCompare(b.start_time)
      : a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time)
  );

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        required_tasks: form.required_tasks.split("\n").map((t) => t.trim()).filter(Boolean),
      };
      await api.post("/schedules", payload);
      toast.success("Visit scheduled");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not schedule visit");
    } finally {
      setSaving(false);
    }
  };

  const action = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    }
  };

  const checkIn = async (s) => {
    const gps = await getGPS();
    await action(
      () => api.post(`/schedules/${s.id}/check-in`, gps || {}),
      `Checked in — ${s.caregiver_name}${gps ? " (GPS captured)" : " (no GPS — location unavailable)"}`
    );
  };
  const cancel = (s) => {
    if (!window.confirm(`Cancel the ${s.visit_type} visit for ${s.client_name} on ${s.date}?`)) return;
    action(() => api.delete(`/schedules/${s.id}`), "Visit cancelled");
  };
  const submitCheckout = async (e) => {
    e.preventDefault();
    const gps = await getGPS();
    await action(
      () => api.post(`/schedules/${checkoutFor.id}/check-out`, {
        notes: checkoutNotes,
        tasks_completed: checkoutTasks,
        ...(gps || {}),
      }),
      "Visit completed — EVV verification ran automatically"
    );
    setCheckoutFor(null);
    setCheckoutNotes("");
    setCheckoutTasks([]);
  };

  const toggleTask = (t) =>
    setCheckoutTasks((arr) => (arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]));

  const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null);

  return (
    <Layout>
      <div data-testid="schedule-page" className="space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Scheduling & Visits</p>
            <h1 className="mt-2 text-3xl font-heading font-light tracking-tight">
              {canManage ? (
                <>Visit <span className="font-bold text-[#2E5C50]">schedule</span></>
              ) : (
                <>Your <span className="font-bold text-[#2E5C50]">visits</span></>
              )}
            </h1>
            <p className="mt-2 text-[#57605A] max-w-2xl">
              {canManage
                ? "Schedule caregiver visits, track check-ins and check-outs. Conflicts are blocked automatically and missed visits raise alerts."
                : "Your scheduled caregiver visits — past and upcoming."}
            </p>
          </div>
          {canManage && (
            <button
              data-testid="new-schedule-btn"
              onClick={() => setOpen(true)}
              className="flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} /> Schedule visit
            </button>
          )}
        </div>

        <div className="flex gap-1.5 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`schedule-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200 ${
                tab === t.key ? "bg-[#2E5C50] text-white" : "bg-white border border-[#E8E6E1] text-[#57605A] hover:border-[#2E5C50]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {schedules === null ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-7 h-7 text-[#2E5C50] animate-spin" strokeWidth={1.5} />
          </div>
        ) : sorted.length === 0 ? (
          <div data-testid="empty-schedules" className="bg-white border border-[#E8E6E1] rounded-lg p-12 text-center">
            <CalendarDays className="w-8 h-8 text-[#C9CFC9] mx-auto" strokeWidth={1.5} />
            <p className="mt-3 text-[#57605A]">
              {canManage ? "No visits in this view. Schedule one or load demo data from the dashboard." : "No visits in this view yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sorted.map((s, i) => (
              <div
                key={s.id}
                data-testid={`schedule-row-${s.id}`}
                className="fade-in-up bg-white border border-[#E8E6E1] rounded-lg p-5 flex flex-wrap items-center gap-4"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div className="w-10 h-10 bg-[#D6E0D9]/40 rounded-md flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-[#2E5C50]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1C211F]">
                    {s.client_name} <span className="text-[#57605A] font-normal">with</span> {s.caregiver_name}
                    {s.is_demo && <span className="ml-2 text-xs text-[#C07A60] font-semibold">DEMO</span>}
                  </p>
                  <p className="text-sm text-[#57605A]">
                    {new Date(`${s.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {s.start_time}–{s.end_time}
                    {s.actual_start && <span> · in {fmtTime(s.actual_start)}</span>}
                    {s.actual_end && <span> / out {fmtTime(s.actual_end)}</span>}
                  </p>
                  {s.visit_notes && <p className="text-xs text-[#57605A] mt-1 italic">"{s.visit_notes}"</p>}
                </div>
                <span className="bg-[#F9F7F4] border border-[#E8E6E1] text-[#57605A] px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                  {s.visit_type}
                </span>
                <span data-testid={`schedule-status-${s.id}`} className={`${STATUS_BADGE[s.status]} px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider whitespace-nowrap`}>
                  {s.status.replace("_", " ")}
                </span>
                {s.status === "completed" && s.evv_status && (
                  <span
                    data-testid={`evv-badge-${s.id}`}
                    className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${
                      s.evv_status === "verified" ? "bg-[#E6F0EB] text-[#34745C]" : "bg-[#FCEAE8] text-[#B54D40]"
                    }`}
                  >
                    EVV {s.evv_status}
                  </span>
                )}
                <button
                  data-testid={`visit-detail-${s.id}`}
                  onClick={() => setDetailFor(s)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#57605A] hover:text-[#2E5C50] transition-colors duration-150"
                >
                  <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> Details
                </button>
                {canManage && (
                  <div className="flex gap-2">
                    {s.status === "scheduled" && (
                      <>
                        <button
                          data-testid={`check-in-${s.id}`}
                          onClick={() => checkIn(s)}
                          className="flex items-center gap-1.5 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-3.5 py-2 text-xs font-semibold"
                        >
                          <LogIn className="w-3.5 h-3.5" strokeWidth={1.5} /> Check in
                        </button>
                        <button
                          data-testid={`cancel-schedule-${s.id}`}
                          onClick={() => cancel(s)}
                          className="flex items-center gap-1.5 bg-[#FCEAE8] text-[#B54D40] hover:bg-[#F8D8D4] transition-colors duration-200 rounded-md px-3.5 py-2 text-xs font-semibold"
                        >
                          <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Cancel
                        </button>
                      </>
                    )}
                    {s.status === "in_progress" && (
                      <button
                        data-testid={`check-out-${s.id}`}
                        onClick={() => setCheckoutFor(s)}
                        className="flex items-center gap-1.5 bg-[#C07A60] text-white hover:bg-[#A8654E] transition-colors duration-200 rounded-md px-3.5 py-2 text-xs font-semibold"
                      >
                        <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Check out
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New schedule dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#F9F7F4] border-[#E8E6E1] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Schedule a visit</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 mt-2">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Client</label>
              <select data-testid="schedule-client-select" required value={form.client_id} onChange={set("client_id")} className={`${inputCls} mt-1`}>
                <option value="">Select a client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Caregiver</label>
              <select data-testid="schedule-caregiver-select" required value={form.caregiver_id} onChange={set("caregiver_id")} className={`${inputCls} mt-1`}>
                <option value="">Select a caregiver…</option>
                {caregivers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.title ? ` — ${c.title}` : ""}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Date</label>
                <input data-testid="schedule-date-input" required type="date" value={form.date} onChange={set("date")} className={`${inputCls} mt-1`} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Start</label>
                <input data-testid="schedule-start-input" required type="time" value={form.start_time} onChange={set("start_time")} className={`${inputCls} mt-1`} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">End</label>
                <input data-testid="schedule-end-input" required type="time" value={form.end_time} onChange={set("end_time")} className={`${inputCls} mt-1`} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Visit type</label>
              <select data-testid="schedule-type-select" value={form.visit_type} onChange={set("visit_type")} className={`${inputCls} mt-1`}>
                {visitTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <textarea data-testid="schedule-notes-input" placeholder="Notes for the caregiver (optional)" rows={2} value={form.notes} onChange={set("notes")} className={inputCls} />
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Required tasks (one per line)</label>
              <textarea
                data-testid="schedule-tasks-input"
                placeholder={"Bathing assistance\nMedication reminder\nMeal preparation"}
                rows={3}
                value={form.required_tasks}
                onChange={set("required_tasks")}
                className={`${inputCls} mt-1`}
              />
            </div>
            <button
              data-testid="save-schedule-btn"
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-6 py-3 font-medium disabled:opacity-60"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
              Schedule visit
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Check-out dialog */}
      <Dialog open={!!checkoutFor} onOpenChange={(v) => !v && setCheckoutFor(null)}>
        <DialogContent className="bg-[#F9F7F4] border-[#E8E6E1] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Check out — {checkoutFor?.client_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCheckout} className="space-y-3 mt-2">
            {(checkoutFor?.required_tasks || []).length > 0 && (
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Tasks completed</label>
                <div className="mt-2 space-y-2">
                  {checkoutFor.required_tasks.map((t) => (
                    <label key={t} data-testid={`checkout-task-${t}`} className="flex items-center gap-2.5 bg-white border border-[#E8E6E1] rounded-md px-3 py-2 text-sm cursor-pointer hover:border-[#2E5C50]/50 transition-colors duration-150">
                      <input type="checkbox" checked={checkoutTasks.includes(t)} onChange={() => toggleTask(t)} className="accent-[#2E5C50] w-4 h-4" />
                      {t}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <textarea
              data-testid="checkout-notes-input"
              placeholder="Visit notes — tasks completed, observations… (optional)"
              rows={4}
              value={checkoutNotes}
              onChange={(e) => setCheckoutNotes(e.target.value)}
              className={inputCls}
            />
            <button
              data-testid="confirm-checkout-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-6 py-3 font-medium"
            >
              Complete visit
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Visit detail / EVV dialog */}
      <VisitDetailDialog
        schedule={detailFor}
        onClose={() => setDetailFor(null)}
        canManage={canManage}
        onUpdated={(d) => {
          setDetailFor(d);
          load();
        }}
      />
    </Layout>
  );
}
